import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import DeviceThermostatOutlinedIcon from "@mui/icons-material/DeviceThermostatOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { off, onValue, ref } from "firebase/database";
import { useParams } from "react-router-dom";

import { database } from "../../firebase";
import MetricCard from "../ui/MetricCard";
import RecordDialog from "../ui/RecordDialog";
import EmptyState from "../ui/EmptyState";
import Chart from "../charts/Chart";
import {
  baseChartOption,
  chartInk,
  lineSeries,
  metricColor,
  withAlpha,
} from "../charts/chartTheme";
import {
  EM_DASH,
  describe,
  formatNumber,
  formatRelative,
  formatTime,
  formatTimestamp,
  latestOf,
  monthlyAverage,
} from "../../utils/format";

const SkinTemperature = ({ goal = 32, title = "Skin temperature", extraActions, menu }) => {
  const { watchId } = useParams();
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const nodeRef = ref(database, `Health/${watchId}/skinTemperature`);

    const handler = onValue(nodeRef, (snapshot) => {
      if (!snapshot.exists()) {
        setRecords([]);
        setSelectedRecord(null);
        return;
      }

      const parsed = Object.values(snapshot.val())
        .filter(
          (entry) =>
            entry &&
            typeof entry === "object" &&
            entry.skinTemperature !== undefined &&
            entry.timestamp
        )
        .map((entry) => ({ ...entry, timestamp: Number(entry.timestamp) }))
        .sort((a, b) => b.timestamp - a.timestamp);

      setRecords(parsed);
      setSelectedRecord((previous) => {
        if (!previous) return parsed[0] ?? null;
        return (
          parsed.find((record) => record.timestamp === previous.timestamp) ??
          parsed[0] ??
          null
        );
      });
    });

    return () => off(nodeRef, "value", handler);
  }, [watchId]);

  const series = useMemo(
    () => records.slice().sort((a, b) => a.timestamp - b.timestamp),
    [records]
  );
  const values = useMemo(
    () => series.map((r) => Number(Number(r.skinTemperature).toFixed(2))),
    [series]
  );
  const stats = useMemo(() => describe(values, 2), [values]);

  const avgYTD = useMemo(() => {
    const year = new Date().getFullYear();
    const inYear = records
      .filter((r) => new Date(r.timestamp).getFullYear() === year)
      .map((r) => Number(r.skinTemperature))
      .filter(Number.isFinite);
    if (!inYear.length) return EM_DASH;
    return (inYear.reduce((sum, n) => sum + n, 0) / inYear.length).toFixed(2);
  }, [records]);

  const accent = metricColor("skinTemperature");
  const selectedIndex = selectedRecord
    ? series.findIndex((r) => r.timestamp === selectedRecord.timestamp)
    : -1;

  const buildOption = () => {
    const color = metricColor("skinTemperature");
    const line = lineSeries({
      name: "Skin temperature",
      data: values,
      color,
      showSymbol: values.length <= 40,
    });

    line.markLine = {
      silent: true,
      symbol: "none",
      label: { show: false },
      data: [
        {
          yAxis: goal,
          lineStyle: { color: withAlpha(color, 0.5), type: [5, 5], width: 1.5 },
          label: {
            show: true,
            formatter: `Target ${goal}°C`,
            position: "insideEndTop",
            color: chartInk().label,
            fontSize: 11,
          },
        },
        ...(selectedIndex >= 0
          ? [
              {
                xAxis: selectedIndex,
                lineStyle: { color: withAlpha(color, 0.4), type: [4, 4], width: 1 },
              },
            ]
          : []),
      ],
    };

    if (selectedIndex >= 0) {
      line.markPoint = {
        symbol: "circle",
        symbolSize: 11,
        itemStyle: { color, borderColor: "#fff", borderWidth: 2 },
        label: { show: false },
        data: [{ coord: [selectedIndex, values[selectedIndex]] }],
      };
    }

    return baseChartOption({
      categories: series.map((r) => formatTime(r.timestamp)),
      series: [line],
      yName: "°C",
      tooltipFormatter: (params) => {
        const point = params?.[0];
        if (!point) return "";
        return `${point.axisValue}<br/><strong>${point.value} °C</strong>`;
      },
    });
  };

  return (
    <>
      <MetricCard
        accent={accent}
        icon={<DeviceThermostatOutlinedIcon />}
        title={title}
        meta={
          <>
            <span className="wg-chip wg-chip--accent">
              {selectedRecord ? "Record loaded" : "No records"}
            </span>
            {selectedRecord && (
              <span className="wg-chip">
                {formatTimestamp(selectedRecord.timestamp)}
              </span>
            )}
          </>
        }
        menu={menu}
        actions={
          <>
            {extraActions}
            <Button
              size="small"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => setSelectedRecord(latestOf(records))}
              disabled={!records.length}
            >
              Latest
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDialogOpen(true)}
              disabled={!records.length}
            >
              Select record
            </Button>
          </>
        }
        note={selectedRecord?.skinTemperatureStatusDescription}
        stats={[
          {
            label: "Selected",
            value: formatNumber(selectedRecord?.skinTemperature, 2),
            unit: "°C",
            meta: selectedRecord?.displayName || "Unassigned",
          },
          {
            label: "Average",
            value: stats.avg,
            unit: "°C",
            // The configured goal is already drawn and labelled on the chart,
            // so it rides along here instead of taking a whole tile — that
            // keeps this panel's four tiles matching every other one.
            meta: `Target ${goal}°C`,
          },
          { label: "Range", value: stats.range, unit: "°C", meta: "Observed window" },
          {
            label: "This month",
            value: monthlyAverage(records, (r) => r.skinTemperature, 2),
            unit: "°C",
            meta: `Year to date ${avgYTD}°C`,
          },
        ]}
        empty={
          !series.length ? (
            <EmptyState
              icon={<DeviceThermostatOutlinedIcon />}
              title="No skin temperature data yet"
              copy="Readings appear here as a trend against your target temperature."
            />
          ) : null
        }
      >
        <Chart buildOption={buildOption} height={300} deps={[values, selectedIndex, goal]} />
      </MetricCard>

      <RecordDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(record) => {
          setSelectedRecord(record);
          setDialogOpen(false);
        }}
        records={records}
        accent={accent}
        title="Skin temperature records"
        getTitle={(record) => `${formatNumber(record.skinTemperature, 2)} °C`}
        getMeta={(record) =>
          `${formatTimestamp(record.timestamp)} · ${formatRelative(record.timestamp)}`
        }
        isSelected={(record) => selectedRecord?.timestamp === record.timestamp}
      />
    </>
  );
};

export default SkinTemperature;
